import type { ThreadSearchResult } from '../hooks/useThreadSearch';
import styles from './ThreadSearchResults.module.css';

interface ThreadSearchResultsProps {
  results: ThreadSearchResult[];
  totalMatches: number;
  loading: boolean;
  error: string | null;
  query: string;
  isAdmin: boolean;
  activeThreadId: string | null;
  onSelect: (sessionKey: string) => void;
}

const MATCH_LABEL: Record<ThreadSearchResult['matchType'], string> = {
  name: 'Name',
  email_subject: 'Email subject',
  email_body: 'Email body',
};

/** Render snippet text with **bold** markdown converted to <strong> highlights. */
function HighlightedSnippet({ text }: { text: string }) {
  if (!text) return null;
  // Split on **...** while preserving the matched chunks.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <mark key={i} className={styles.mark}>
              {part.slice(2, -2)}
            </mark>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

export function ThreadSearchResults({
  results,
  totalMatches,
  loading,
  error,
  query,
  isAdmin,
  activeThreadId,
  onSelect,
}: ThreadSearchResultsProps) {
  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <span className={styles.scopeChip} title={isAdmin ? 'Admin: searching every thread on the server' : 'Searching threads you can read'}>
          {isAdmin ? 'Searching all threads' : 'Searching your threads'}
        </span>
        <span className={styles.count}>
          {loading ? 'searching…' : `${totalMatches} match${totalMatches === 1 ? '' : 'es'}`}
        </span>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!loading && !error && results.length === 0 && (
        <div className={styles.empty}>
          No threads match <q>{query}</q>.
        </div>
      )}

      <div className={styles.list}>
        {results.map((r) => {
          const isActive = r.sessionKey === activeThreadId;
          return (
            <button
              key={r.sessionKey}
              type="button"
              className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
              onClick={() => onSelect(r.sessionKey)}
            >
              <div className={styles.itemHeader}>
                <span className={styles.name}>{r.name || r.sessionKey}</span>
                <span className={styles.matchType}>{MATCH_LABEL[r.matchType]}</span>
              </div>
              <div className={styles.snippet}>
                <HighlightedSnippet text={r.snippet} />
              </div>
              <div className={styles.meta}>
                <span className={styles.owner}>{r.owner}</span>
                {r.sharedWith === '*' ? (
                  <span className={styles.scope}>org</span>
                ) : Array.isArray(r.sharedWith) && r.sharedWith.length > 0 ? (
                  <span className={styles.scope}>shared +{r.sharedWith.length}</span>
                ) : (
                  <span className={styles.scope}>private</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
